import React, { useMemo, useRef, useState } from 'react';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import { Play, X, Quote, Star, ArrowRight } from 'lucide-react';

import SectionHeading from '@/components/brand/SectionHeading';
import { PrimaryButton } from '@/components/brand/Buttons';
import TestimonialModal from '@/components/brand/TestimonialModal';
import CtaBand from '@/components/sections/CtaBand';
import { viewportOnce } from '@/lib/motion';
import { useReviewsData } from '@/hooks/useReviewsData';

const EASE = [0.22, 1, 0.36, 1];
const PAGE_SIZE = 9;

/* ─── Static fallback ─────────────────────────────────────────────────── */
const FALLBACK = {
  sections: {
    reviewsHeader: {
      data: {
        kicker: 'Testimonials',
        heading: 'Client Speaks',
        subhead: "Take Our Clients' Word for It",
      },
    },
  },
  collections: { reviews: [] },
};

/* ─── YouTube / Vimeo embed URL ───────────────────────────────────────── */
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be'))
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}?autoplay=1`;
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname.startsWith('/embed/'))
        return `${url}${url.includes('?') ? '&' : '?'}autoplay=1`;
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=1`;
    }
    if (u.hostname.includes('vimeo.com'))
      return `https://player.vimeo.com/video/${u.pathname.slice(1)}?autoplay=1`;
    return url;
  } catch {
    return null;
  }
}

/* ─── Stars ───────────────────────────────────────────────────────────── */
function StarRow({ count = 5, hov = false }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <motion.span
          key={i}
          animate={hov ? { scale: [1, 1.3, 1], y: [0, -3, 0] } : { scale: 1, y: 0 }}
          transition={{ duration: 0.38, delay: i * 0.045, ease: EASE }}
        >
          <Star
            className={`h-4 w-4 ${i < count ? 'text-[#E8C25A] fill-[#E8C25A]' : 'text-slatebody/25'}`}
            strokeWidth={1.5}
          />
        </motion.span>
      ))}
    </div>
  );
}

/* ─── Shared: spinning conic-gradient border layer ────────────────────── */
function SpinBorder({ active, speed = 3, colors }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      animate={active && !reduced ? { rotate: 360, opacity: 1 } : { rotate: 0, opacity: 0 }}
      transition={{
        rotate: { duration: speed, repeat: Infinity, ease: 'linear' },
        opacity: { duration: 0.35 },
      }}
      style={{ background: colors }}
    />
  );
}

/* ─── Text testimonial card ───────────────────────────────────────────── */
function TextCard({ r, i, onClick }) {
  const cardRef = useRef(null);
  const reduced = useReducedMotion();
  const [hov, setHov] = useState(false);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-1, 1], [7, -7]), { stiffness: 180, damping: 20 });
  const rotY = useSpring(useTransform(mx, [-1, 1], [-9, 9]), { stiffness: 180, damping: 20 });
  const glowX = useTransform(mx, [-1, 1], ['5%', '95%']);
  const glowY = useTransform(my, [-1, 1], ['5%', '95%']);

  const onMove = (e) => {
    if (reduced) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
    my.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };
  const onLeave = () => { mx.set(0); my.set(0); setHov(false); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ duration: 0.55, delay: (i % PAGE_SIZE) * 0.055, ease: EASE }}
      style={{ perspective: '900px' }}
    >
      <motion.div
        style={{
          rotateX: reduced ? 0 : rotX,
          rotateY: reduced ? 0 : rotY,
          transformStyle: 'preserve-3d',
          willChange: 'transform',
        }}
      >
        {/* 1.5 px animated-border wrapper */}
        <div className="relative rounded-2xl overflow-hidden p-[1.5px]">
          <SpinBorder
            active={hov}
            speed={3.2}
            colors="conic-gradient(from 0deg, #C9962E 0deg, #E8C25A 80deg, #2F6FB0 180deg, #537EA8 260deg, #C9962E 360deg)"
          />
          {/* static hairline when not hovered */}
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-2xl border border-hairline pointer-events-none"
            animate={{ opacity: hov ? 0 : 1 }}
            transition={{ duration: 0.3 }}
          />

          {/* Card body */}
          <motion.button
            ref={cardRef}
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            aria-label={`Read full testimonial from ${r.name}`}
            data-testid={`reviews-card-text-${i}`}
            className="relative w-full text-left rounded-[14px] bg-white overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60 p-6 sm:p-7 flex flex-col"
            whileHover={reduced ? {} : { y: -8, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          >
            {/* cursor-following bloom */}
            {!reduced && (
              <motion.div
                aria-hidden
                className="pointer-events-none absolute rounded-full"
                style={{
                  left: glowX,
                  top: glowY,
                  width: 230,
                  height: 230,
                  translateX: '-50%',
                  translateY: '-50%',
                  background:
                    'radial-gradient(circle, rgba(232,194,90,0.30) 0%, rgba(201,150,46,0.10) 45%, transparent 70%)',
                }}
                animate={{ opacity: hov ? 1 : 0 }}
                transition={{ duration: 0.35 }}
              />
            )}

            {/* Ambient bg wash on hover */}
            <motion.div
              aria-hidden
              className="absolute inset-0 rounded-[14px] pointer-events-none"
              animate={{ opacity: hov ? 1 : 0 }}
              transition={{ duration: 0.45 }}
              style={{
                background:
                  'linear-gradient(135deg, rgba(232,194,90,0.055) 0%, rgba(47,111,176,0.04) 100%)',
              }}
            />

            {/* Quote icon */}
            <motion.div
              className="relative"
              animate={
                reduced
                  ? {}
                  : { scale: hov ? 1.28 : 1, rotate: hov ? -14 : 0 }
              }
              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
              style={{ color: hov ? '#C9962E' : '#2F6FB0', transition: 'color 0.3s' }}
            >
              <Quote className="h-7 w-7" />
            </motion.div>

            {/* Stars */}
            <div className="mt-3 relative">
              <StarRow count={r.rating || 5} hov={hov} />
            </div>

            {/* Quote text */}
            <p className="relative mt-4 text-base sm:text-lg text-navy-deep leading-relaxed line-clamp-5 flex-1">
              &ldquo;{r.quote}&rdquo;
            </p>

            {/* Animated divider */}
            <motion.div
              className="my-5 h-px"
              animate={{
                background: hov
                  ? 'linear-gradient(90deg, transparent, rgba(201,150,46,0.55), transparent)'
                  : 'linear-gradient(90deg, transparent, rgba(31,45,69,0.10), transparent)',
              }}
              transition={{ duration: 0.4 }}
            />

            {/* Author */}
            <div className="flex items-center gap-3">
              {r.avatar ? (
                <motion.img
                  src={r.avatar}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  className="h-11 w-11 rounded-full object-cover flex-shrink-0"
                  style={{ boxShadow: '0 0 0 2px white, 0 0 0 3.5px rgba(201,150,46,0.0)' }}
                  animate={{
                    scale: hov ? 1.1 : 1,
                    boxShadow: hov
                      ? '0 0 0 2px white, 0 0 0 3.5px rgba(201,150,46,0.6)'
                      : '0 0 0 2px white, 0 0 0 3.5px rgba(201,150,46,0.0)',
                  }}
                  transition={{ duration: 0.35, ease: EASE }}
                />
              ) : (
                <div className="h-11 w-11 rounded-full bg-sky-100 flex-shrink-0" />
              )}
              <div>
                <p className="text-sm font-bold text-navy-deep">{r.name}</p>
                <p className="text-xs text-slatebody">
                  {[r.role, r.company].filter(Boolean).join(r.role && r.company ? ' of ' : '')}
                  {(r.role || r.company) && r.location ? ' · ' : ''}
                  {r.location || ''}
                </p>
              </div>
            </div>

            {/* Deep shadow bloom — absolutely positioned over entire card */}
            <motion.div
              aria-hidden
              className="absolute inset-0 rounded-[14px] pointer-events-none"
              animate={{
                boxShadow: hov
                  ? '0 24px 64px rgba(15,25,45,0.22), 0 0 36px rgba(201,150,46,0.12)'
                  : '0 4px 18px rgba(15,25,45,0.07)',
              }}
              transition={{ duration: 0.45 }}
            />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Video testimonial card ──────────────────────────────────────────── */
function VideoCard({ r, i, onClick }) {
  const cardRef = useRef(null);
  const reduced = useReducedMotion();
  const [hov, setHov] = useState(false);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-1, 1], [5, -5]), { stiffness: 180, damping: 20 });
  const rotY = useSpring(useTransform(mx, [-1, 1], [-7, 7]), { stiffness: 180, damping: 20 });

  const onMove = (e) => {
    if (reduced) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
    my.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };
  const onLeave = () => { mx.set(0); my.set(0); setHov(false); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ duration: 0.55, delay: (i % PAGE_SIZE) * 0.055, ease: EASE }}
      style={{ perspective: '900px' }}
    >
      <motion.div
        style={{
          rotateX: reduced ? 0 : rotX,
          rotateY: reduced ? 0 : rotY,
          transformStyle: 'preserve-3d',
          willChange: 'transform',
        }}
      >
        <div className="relative rounded-2xl overflow-hidden p-[1.5px]">
          <SpinBorder
            active={hov}
            speed={2.5}
            colors="conic-gradient(from 0deg, #E8C25A 0deg, #C9962E 70deg, #2F6FB0 160deg, #537EA8 240deg, #E8C25A 360deg)"
          />
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-2xl border border-white/10 pointer-events-none"
            animate={{ opacity: hov ? 0 : 1 }}
            transition={{ duration: 0.3 }}
          />

          <motion.button
            ref={cardRef}
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            aria-label={`Play testimonial from ${r.name}`}
            data-testid={`reviews-card-video-${i}`}
            className="relative w-full text-left rounded-[14px] overflow-hidden bg-navy-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60"
            whileHover={reduced ? {} : { y: -8, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          >
            {/* Thumbnail */}
            <div className="relative aspect-[16/9] overflow-hidden">
              <motion.img
                src={r.avatar}
                alt={r.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                animate={{
                  opacity: hov ? 0.88 : 0.60,
                  scale: hov ? 1.04 : 1.12,
                }}
                transition={{ duration: 0.65, ease: EASE }}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-navy-deep/85 via-navy-deep/28 to-transparent" />

              {/* Play button */}
              <span aria-hidden className="absolute inset-0 flex items-center justify-center">
                <motion.span
                  className="relative inline-flex items-center justify-center h-16 w-16 rounded-full bg-white/95 shadow-[0_12px_36px_rgba(0,0,0,0.42)]"
                  animate={{ scale: hov ? 1.22 : 1 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                >
                  <Play className="h-7 w-7 text-navy-deep translate-x-[1px]" fill="#0F192D" />

                  {/* Expanding pulse rings on hover */}
                  {hov && !reduced && (
                    <>
                      <motion.span
                        className="absolute inset-0 rounded-full border-2 border-white/55"
                        animate={{ scale: [1, 1.9], opacity: [0.7, 0] }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                      />
                      <motion.span
                        className="absolute inset-0 rounded-full border-[1.5px] border-white/35"
                        animate={{ scale: [1, 2.4], opacity: [0.5, 0] }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut', delay: 0.28 }}
                      />
                    </>
                  )}
                  {/* Static single ring when idle */}
                  {!hov && (
                    <span className="absolute inset-0 rounded-full ring-2 ring-white/35 animate-ping" />
                  )}
                </motion.span>
              </span>
            </div>

            {/* Info */}
            <div className="p-5">
              <StarRow count={r.rating || 5} hov={hov} />
              <p className="mt-3 text-white text-sm sm:text-base leading-relaxed line-clamp-2">
                &ldquo;{r.quote}&rdquo;
              </p>
              <div className="mt-4 flex items-center gap-3">
                <motion.img
                  src={r.avatar}
                  alt=""
                  aria-hidden
                  className="h-9 w-9 rounded-full object-cover ring-2 ring-white/20 flex-shrink-0"
                  animate={{ scale: hov ? 1.1 : 1 }}
                  transition={{ duration: 0.3 }}
                />
                <div>
                  <p className="text-sm font-semibold text-white">{r.name}</p>
                  <p className="text-xs text-white/65">
                    {r.role}
                    {r.role && r.company ? ' · ' : ''}
                    {r.company}
                  </p>
                </div>
              </div>
            </div>

            {/* Shadow bloom */}
            <motion.div
              aria-hidden
              className="absolute inset-0 rounded-[14px] pointer-events-none"
              animate={{
                boxShadow: hov
                  ? '0 28px 68px rgba(15,25,45,0.38), 0 0 40px rgba(232,194,90,0.14)'
                  : '0 10px 30px rgba(15,25,45,0.22)',
              }}
              transition={{ duration: 0.45 }}
            />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────── */
export default function Reviews() {
  const { data, loading } = useReviewsData();
  const reduced = useReducedMotion();

  const payload = data || FALLBACK;
  const header =
    payload.sections?.reviewsHeader?.data || FALLBACK.sections.reviewsHeader.data;
  const reviews = payload.collections?.reviews || [];

  /* Interleave video + text into one unified list */
  const allReviews = useMemo(() => {
    const videos = reviews.filter((r) => r.videoUrl);
    const texts = reviews.filter((r) => !r.videoUrl);

    if (videos.length === 0) return texts.map((r) => ({ ...r, _type: 'text' }));
    if (texts.length === 0) return videos.map((r) => ({ ...r, _type: 'video' }));

    const result = [];
    const gap = Math.max(1, Math.ceil(texts.length / videos.length));
    let vi = 0;
    let ti = 0;

    while (vi < videos.length || ti < texts.length) {
      if (vi < videos.length) result.push({ ...videos[vi++], _type: 'video' });
      for (let j = 0; j < gap && ti < texts.length; j++)
        result.push({ ...texts[ti++], _type: 'text' });
    }
    return result;
  }, [reviews]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [openVideo, setOpenVideo] = useState(null);
  const [openTestimonial, setOpenTestimonial] = useState(null);

  const shownItems = allReviews.slice(0, visibleCount);
  const hasMore = visibleCount < allReviews.length;

  return (
    <>
      {/* ─── Hero header ──────────────────────────────────────────────── */}
      {(!data || data?.sections?.reviewsHeader) && <section
        className="relative pt-36 pb-8 sm:pt-44 sm:pb-12 overflow-hidden bg-sky-50"
        data-testid="reviews-header"
      >
        {/* Decorative mesh gradients */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-70 pointer-events-none"
          style={{
            background:
              'radial-gradient(700px 420px at 10% 16%, rgba(47,111,176,0.11), transparent 65%), radial-gradient(560px 320px at 90% 88%, rgba(232,194,90,0.10), transparent 70%)',
          }}
        />
        {/* Dot-grid texture */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, #0F192D 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative container mx-auto">
          <SectionHeading
            eyebrow={header.kicker || 'Testimonials'}
            title={header.heading || 'Client Speaks'}
            subtitle={header.subhead || "Take Our Clients' Word for It"}
            align="center"
          />
          {(header.description || header.subtext) && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.45, ease: EASE }}
              className="mt-5 max-w-2xl mx-auto text-center text-sm sm:text-base text-slatebody leading-relaxed"
            >
              {header.description || header.subtext}
            </motion.p>
          )}
        </div>

        {/* Gold hairline bottom accent */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#E8C25A]/40 to-transparent" />
      </section>}

      {/* ─── Unified mixed testimonials grid ──────────────────────────── */}
      <section
        className="py-16 sm:py-24 bg-sky-50"
        data-testid="reviews-grid-section"
      >
        <div className="container mx-auto">
          {allReviews.length === 0 && !loading && (
            <p className="mt-4 text-center text-base sm:text-lg text-slatebody">
              We&rsquo;re collecting fresh testimonials — check back soon.
            </p>
          )}

          {shownItems.length > 0 && (
            <>
              <motion.div
                layout={!reduced}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {shownItems.map((r, i) =>
                  r._type === 'video' ? (
                    <VideoCard
                      key={r.id || r.name + i}
                      r={r}
                      i={i}
                      onClick={() => setOpenVideo(r)}
                    />
                  ) : (
                    <TextCard
                      key={r.id || r.name + i}
                      r={r}
                      i={i}
                      onClick={() => setOpenTestimonial(r)}
                    />
                  )
                )}
              </motion.div>

              {hasMore && (
                <div className="mt-14 text-center">
                  <PrimaryButton
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    data-testid="reviews-load-more"
                  >
                    Load More&nbsp;
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </PrimaryButton>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <CtaBand />

      {/* ─── Video lightbox ───────────────────────────────────────────── */}
      <AnimatePresence>
        {openVideo && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Video testimonial"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-deep/85 backdrop-blur-sm p-4"
            onClick={() => setOpenVideo(null)}
            data-testid="reviews-video-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 14, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.32, ease: EASE }}
              className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black ring-1 ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.60)]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close video"
                onClick={() => setOpenVideo(null)}
                className="absolute top-3 right-3 z-10 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-navy-deep transition-colors"
                data-testid="reviews-video-close"
              >
                <X className="h-4 w-4" />
              </button>
              {toEmbedUrl(openVideo.videoUrl) ? (
                <iframe
                  src={toEmbedUrl(openVideo.videoUrl)}
                  title={`Testimonial — ${openVideo.name}`}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                  Unable to load this video right now.
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Text testimonial modal ───────────────────────────────────── */}
      <TestimonialModal
        testimonial={openTestimonial}
        onClose={() => setOpenTestimonial(null)}
      />
    </>
  );
}
